(() => {
  if (window.ltifyWishlistPageLoaded) return;
  window.ltifyWishlistPageLoaded = true;

  const storageKey = 'ltify_wishlist_v1';
  const states = new WeakMap();

  function readHandles() {
    const saved = JSON.parse(
      localStorage.getItem(storageKey) || '[]'
    );

    if (!Array.isArray(saved)) return [];

    return [...new Set(
      saved.filter(handle =>
        typeof handle === 'string' && handle.length > 0
      )
    )];
  }

  async function renderPage(page) {
    const grid = page.querySelector('[data-wishlist-grid]');
    const status = page.querySelector('[data-wishlist-status]');
    const empty = page.querySelector('[data-wishlist-empty]');

    if (!grid || !status || !empty) return;

    let handles;

    try {
      handles = readHandles();
    } catch (error) {
      status.hidden = false;
      status.textContent =
        'Unable to read your wishlist. Please allow browser storage.';
      return;
    }

    const signature = JSON.stringify(handles);
    const previous = states.get(page);

    if (previous && previous.signature === signature) return;
    if (previous) previous.controller.abort();

    const state = {
      signature,
      controller: new AbortController()
    };

    states.set(page, state);
    grid.replaceChildren();
    empty.hidden = true;
    status.hidden = false;

    if (handles.length === 0) {
      status.hidden = true;
      empty.hidden = false;
      return;
    }

    status.textContent = 'Loading your wishlist...';

    const root = window.Shopify?.routes?.root || '/';
    let failed = 0;

    for (const handle of handles) {
      if (states.get(page) !== state || !page.isConnected) return;

      try {
        const url = new URL(
          `${root}products/${encodeURIComponent(handle)}`,
          window.location.origin
        );

        url.searchParams.set('view', 'ltify-wishlist-card');

        const response = await fetch(url, {
          signal: state.controller.signal,
          credentials: 'same-origin'
        });

        if (!response.ok) throw new Error('Product unavailable');

        const html = await response.text();
        const documentFragment = new DOMParser()
          .parseFromString(html, 'text/html');

        const card = documentFragment.querySelector(
          '[data-wishlist-item]'
        );

        if (!card || card.dataset.wishlistItem !== handle) {
          throw new Error('Missing product card');
        }

        if (states.get(page) !== state || !page.isConnected) return;

        card.querySelectorAll('script').forEach(script => {
          script.remove();
        });

        card.querySelectorAll('.ltify-wishlist-button')
          .forEach(button => {
            button.setAttribute('aria-pressed', 'true');
            button.setAttribute(
              'aria-label',
              'Remove from wishlist'
            );
          });

        grid.appendChild(document.importNode(card, true));
      } catch (error) {
        if (error.name === 'AbortError') return;
        failed += 1;
      }
    }

    if (states.get(page) !== state) return;

    if (failed > 0) {
      status.textContent =
        'Some saved products could not be loaded. Please refresh to try again.';
      status.hidden = false;
      states.delete(page);
    } else {
      status.hidden = true;
    }
  }

  function renderAllPages() {
    document.querySelectorAll('[data-ltify-wishlist-page]')
      .forEach(page => {
        renderPage(page);
      });
  }

  document.addEventListener(
    'ltify:wishlist-updated',
    renderAllPages
  );

  document.addEventListener(
    'shopify:section:load',
    renderAllPages
  );

  window.addEventListener('pageshow', renderAllPages);

  window.addEventListener('storage', event => {
    if (event.key === storageKey || event.key === null) {
      renderAllPages();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      renderAllPages,
      { once: true }
    );
  } else {
    renderAllPages();
  }
})();

(() => {
  if (window.ltifyWishlistFiltersLoaded) return;
  window.ltifyWishlistFiltersLoaded = true;

  function setup(page) {
    if (page.dataset.filtersReady) return;
    page.dataset.filtersReady = 'true';

    const grid = page.querySelector('[data-wishlist-grid]');
    const filters = page.querySelector('[data-wishlist-filters]');
    const groups = page.querySelector('[data-wishlist-filter-groups]');
    const toolbar = page.querySelector('[data-wishlist-toolbar]');
    const sort = page.querySelector('[data-wishlist-sort]');
    const results = page.querySelector('[data-wishlist-results]');
    const noResults = page.querySelector('[data-wishlist-no-results]');
    const clear = page.querySelector('[data-wishlist-clear]');

    if (!grid || !filters || !groups || !toolbar ||
        !sort || !results || !noResults) return;

    const definitions = [
      ['wishlistRecipients', 'Recipients'],
      ['wishlistOccasions', 'Occasions'],
      ['wishlistGiftType', 'Gift type']
    ];

    const selected = new Map(
      definitions.map(([key]) => [key, new Set()])
    );

    const originalOrder = new WeakMap();
    let nextOrder = 0;
    let lastSignature = null;

    function values(card, key) {
      try {
        const value = JSON.parse(card.dataset[key] || 'null');
        const list = Array.isArray(value) ? value : [value];

        return [...new Set(list.filter(item =>
          typeof item === 'string' && item.trim()
        ).map(item => item.trim()))];
      } catch (error) {
        return [];
      }
    }

    function cards() {
      return Array.from(grid.children).filter(card =>
        card.hasAttribute('data-wishlist-item')
      );
    }

    function renderActiveFilters() {
  const container = page.querySelector(
    '[data-wishlist-active-filters]'
  );

  if (!container) return;

  const filterPanel = page.querySelector(
    '.ltify-wishlist-filter-panel'
  );

  if (filterPanel && container.previousElementSibling !== filterPanel) {
    filterPanel.after(container);
  }

  container.replaceChildren();

  definitions.forEach(([key, title]) => {
    const label =
      key === 'wishlistOccasions' ? 'Occasion' :
      key === 'wishlistGiftType' ? 'Product type' :
      title;

    selected.get(key).forEach(value => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'ltify-wishlist-filter-chip';
      chip.textContent = `${label}: ${value} ×`;
      chip.setAttribute(
        'aria-label',
        `Remove filter: ${label}, ${value}`
      );

      chip.addEventListener('click', () => {
        selected.get(key).delete(value);
        lastSignature = null;
        rebuild();
      });

      container.appendChild(chip);
    });
  });

  const hasSelections = container.children.length > 0;
  container.hidden = !hasSelections;

  if (hasSelections) {
    const removeAll = document.createElement('button');
    removeAll.type = 'button';
    removeAll.className = 'ltify-wishlist-chips-clear';
    removeAll.textContent = 'Remove all';

    removeAll.addEventListener('click', () => {
      selected.forEach(choices => choices.clear());
      lastSignature = null;
      rebuild();
    });

    container.appendChild(removeAll);
  }
}
    function apply() {
      const items = cards();

      items.forEach(card => {
        card.hidden = definitions.some(([key]) => {
          const choices = selected.get(key);
          return choices.size > 0 &&
            !values(card, key).some(value => choices.has(value));
        });
      });

      const ordered = [...items].sort((a, b) => {
        const fallback =
          originalOrder.get(a) - originalOrder.get(b);

        if (sort.value === 'price-asc') {
          return Number(a.dataset.wishlistPrice) -
            Number(b.dataset.wishlistPrice) || fallback;
        }

        if (sort.value === 'price-desc') {
          return Number(b.dataset.wishlistPrice) -
            Number(a.dataset.wishlistPrice) || fallback;
        }

        if (sort.value === 'title-asc') {
          return (a.dataset.wishlistTitle || '').localeCompare(
            b.dataset.wishlistTitle || ''
          ) || fallback;
        }

        return fallback;
      });

      const needsReorder = ordered.some(
        (card, index) => card !== items[index]
      );

      if (needsReorder) {
        ordered.forEach(card => grid.appendChild(card));
      }

      const visible = items.filter(card => !card.hidden).length;
      results.textContent =
        `${visible} ${visible === 1 ? 'result' : 'results'}`;

      toolbar.hidden = items.length === 0;
      noResults.hidden = items.length === 0 || visible > 0;
      renderActiveFilters();
    }

    function rebuild() {
      const items = cards();

      items.forEach(card => {
        if (!originalOrder.has(card)) {
          originalOrder.set(card, nextOrder++);
        }
      });

      const signature = JSON.stringify(
        items.map(card => card.dataset.wishlistItem).sort()
      );

      if (signature === lastSignature) return;
      lastSignature = signature;
      groups.replaceChildren();

      definitions.forEach(([key, title]) => {
        const counts = new Map();

        items.forEach(card => {
          values(card, key).forEach(value => {
            counts.set(value, (counts.get(value) || 0) + 1);
          });
        });

        const choices = selected.get(key);
        Array.from(choices).forEach(value => {
          if (!counts.has(value)) choices.delete(value);
        });

        if (counts.size === 0) return;

        const fieldset = document.createElement('details');
fieldset.className = 'ltify-wishlist-filter-group';
fieldset.open = true;

const legend = document.createElement('summary');
legend.textContent =
  key === 'wishlistOccasions' ? 'Occasion' :
  key === 'wishlistGiftType' ? 'Product type' :
  title;

fieldset.appendChild(legend);


        Array.from(counts.keys()).sort((a, b) =>
          a.localeCompare(b)
        ).forEach(value => {
          const label = document.createElement('label');
          label.className = 'ltify-wishlist-filter-option';

          const input = document.createElement('input');
          input.type = 'checkbox';
          input.checked = choices.has(value);

          const text = document.createElement('span');
          text.textContent = `${value} (${counts.get(value)})`;

          input.addEventListener('change', () => {
            if (input.checked) choices.add(value);
            else choices.delete(value);
            apply();
          });

          label.append(input, text);
          fieldset.appendChild(label);
        });

        groups.appendChild(fieldset);
      });

      filters.hidden = groups.children.length === 0;
      apply();
    }

    sort.addEventListener('change', apply);

    clear?.addEventListener('click', () => {
      selected.forEach(choices => choices.clear());
      groups.querySelectorAll('input').forEach(input => {
        input.checked = false;
      });
      apply();
    });

    const panel = page.querySelector(
  '.ltify-wishlist-filter-panel'
);
const drawer = page.querySelector('[data-wishlist-drawer]');
const drawerBody = page.querySelector(
  '[data-wishlist-drawer-body]'
);
const drawerResults = page.querySelector(
  '[data-wishlist-drawer-results]'
);
const mobileView = window.matchMedia('(max-width: 749px)');

if (panel && drawer && drawerBody) {
  const summary = panel.querySelector('summary');
  const sortLabel = sort.closest('label');
  let previousOverflow = null;

  function closeDrawer() {
    if (drawer.open) drawer.close();
  }

  function updateFilterPanel() {
  if (!summary || !sortLabel) return;

  let mobileSort = drawerBody.querySelector(
    '[data-wishlist-mobile-sort]'
  );

  if (mobileView.matches) {
    panel.open = false;

    toolbar.appendChild(sortLabel);
    drawerBody.appendChild(groups);
    summary.appendChild(results);

    if (!mobileSort) {
      mobileSort = document.createElement('details');
      mobileSort.className = 'ltify-wishlist-filter-group';
      mobileSort.dataset.wishlistMobileSort = '';
      mobileSort.open = true;

      const heading = document.createElement('summary');
      mobileSort.appendChild(heading);

      const options = [
        ['saved', 'Relevance'],
        ['price-asc', 'Price, low to high'],
        ['price-desc', 'Price, high to low']
      ];

      const radioName =
        `wishlist-sort-${Math.random().toString(36).slice(2)}`;

      options.forEach(([value, text]) => {
        const label = document.createElement('label');
        label.className = 'ltify-wishlist-filter-option';

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = radioName;
        input.value = value;

        const caption = document.createElement('span');
        caption.textContent = text;

        input.addEventListener('change', () => {
          if (!input.checked) return;

          sort.value = value;
          sort.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        });

        label.append(input, caption);
        mobileSort.appendChild(label);
      });

      drawerBody.appendChild(mobileSort);

      sort.addEventListener('change', syncMobileSort);
    }

    drawerBody.appendChild(mobileSort);
    syncMobileSort();
  } else {
    closeDrawer();
    panel.open = true;

    toolbar.append(sortLabel, results);
    panel.appendChild(groups);

    if (mobileSort) mobileSort.hidden = true;
  }

  function syncMobileSort() {
    if (!mobileSort) return;

    mobileSort.hidden = !mobileView.matches;

    const currentText =
      sort.options[sort.selectedIndex]?.textContent ||
      'Relevance';

    mobileSort.querySelector('summary').textContent =
      `Sort by: ${currentText}`;

    mobileSort.querySelectorAll('input').forEach(input => {
      input.checked = input.value === sort.value;
    });
  }
}

  summary.addEventListener('click', event => {
    if (!mobileView.matches) return;

    event.preventDefault();

    if (!drawer.open) {
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      drawer.showModal();
    }
  });

  drawer.addEventListener('close', () => {
    if (previousOverflow !== null) {
      document.body.style.overflow = previousOverflow;
      previousOverflow = null;
    }

    if (mobileView.matches) summary.focus();
  });

  drawer.addEventListener('click', event => {
    if (event.target !== drawer) return;

    const bounds = drawer.getBoundingClientRect();
    const outside =
      event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom;

    if (outside) closeDrawer();
  });

  page.querySelector('[data-wishlist-drawer-close]')
    ?.addEventListener('click', closeDrawer);

  page.querySelector('[data-wishlist-apply]')
    ?.addEventListener('click', closeDrawer);

  page.querySelector('[data-wishlist-remove-all]')
    ?.addEventListener('click', () => {
      selected.forEach(choices => choices.clear());

      groups.querySelectorAll('input').forEach(input => {
        input.checked = false;
      });

      apply();
    });

  function syncDrawerResults() {
    if (drawerResults) {
      drawerResults.textContent = results.textContent;
    }
  }

  new MutationObserver(syncDrawerResults).observe(results, {
    childList: true,
    characterData: true,
    subtree: true
  });

  updateFilterPanel();
  syncDrawerResults();
  mobileView.addEventListener('change', updateFilterPanel);
}

new MutationObserver(rebuild).observe(grid, {
  childList: true
});

rebuild();
  }

  function initialize() {
    document.querySelectorAll('[data-ltify-wishlist-page]')
      .forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded', initialize, { once: true }
    );
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();
